import React, { useState } from 'react';
import { useField } from 'formik';
import { Plus, X } from 'lucide-react';
import { NO_FEATURE_ICON } from '@repo/lib';

import { Input } from '@dashboard/components/ui/input';
import { Textarea } from '@dashboard/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@dashboard/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import { cn } from '@dashboard/lib/utils';
import { BADGE_ICONS } from '@repo/checkout/components/BadgeIcon';
import BADGE_ICON_KEYS from '@repo/checkout/lib/badgeIconKeys';

const ICON_LABELS = {
  star: 'Star',
  sparkles: 'Sparkles',
  fire: 'Fire',
  bolt: 'Bolt',
  rocket: 'Rocket',
  trophy: 'Trophy',
  check: 'Tick',
  'x-mark': 'Cross',
  plus: 'Plus',
  'arrow-right': 'Arrow',
  'check-badge': 'Check badge',
  'check-circle': 'Check',
  'shield-check': 'Shield',
  'thumbs-up': 'Thumbs up',
  heart: 'Heart',
  smile: 'Smile',
  'light-bulb': 'Light bulb',
  sun: 'Sun',
  moon: 'Moon',
  gift: 'Gift',
  tag: 'Tag',
  percent: 'Percent',
  dollar: 'Dollar',
  banknotes: 'Banknotes',
  'credit-card': 'Credit card',
  'shopping-bag': 'Shopping bag',
  'shopping-cart': 'Shopping cart',
  truck: 'Truck',
  clock: 'Clock',
  calendar: 'Calendar',
  bell: 'Bell',
  megaphone: 'Megaphone',
  'trending-up': 'Trending up',
  chart: 'Chart',
  refresh: 'Refresh',
  lock: 'Lock',
  key: 'Key',
  ticket: 'Ticket',
  users: 'People',
  'academic-cap': 'Graduation cap',
  book: 'Book',
  music: 'Music',
  video: 'Video',
  document: 'Document',
  download: 'Download',
  mobile: 'Mobile',
  camera: 'Camera',
  'paint-brush': 'Paint brush',
  puzzle: 'Puzzle',
  cube: 'Cube',
  globe: 'Globe',
  'map-pin': 'Map pin',
  storefront: 'Storefront',
  tools: 'Tools',
  beaker: 'Beaker',
  lifebuoy: 'Lifebuoy',
  chat: 'Chat',
  envelope: 'Envelope',
  phone: 'Phone',
  cake: 'Cake',
  scale: 'Scale',
  eye: 'Eye',
};

/**
 * The highlight text input with an icon picker sitting inside its left edge.
 * The icon is drawn with the same components the checkout renders, so what the
 * seller picks is what the customer sees. It stays out of the way until wanted:
 * an untouched field is the plain input it always was, and the input comes
 * first in the tab order so typing is never interrupted by the picker. No
 * tooltip: the fields sit too close for one to land anywhere but on a label,
 * and it re-opened on the focus that returns after picking.
 */
export default function HighlightField({
  name = 'prices.0.badge',
  iconName = 'prices.0.badgeIcon',
  placeholder,
  // What the checkout draws while no icon is chosen (a feature line's check),
  // shown muted so the seller sees the default without it reading as picked.
  // Removing then stores NO_FEATURE_ICON rather than null, since null is what
  // draws the fallback; the field reads as empty again, like one with no
  // fallback at all.
  fallbackIcon = null,
  pickerLabel = 'Highlight icon',
  // Names the input, and the icon button after it, where no label does: a
  // list of these fields needs each button to read as its own row's.
  ariaLabel,
  maxLength,
  // A box of copy rather than a line; the picker pins to the first line.
  multiline = false,
}) {
  const [textField] = useField(name);
  const [iconField, , iconHelpers] = useField(iconName);
  const [open, setOpen] = useState(false);

  const icon = BADGE_ICONS[iconField.value] ? iconField.value : null;
  const Icon = icon ? BADGE_ICONS[icon] : null;
  const removed = Boolean(fallbackIcon) && iconField.value === NO_FEATURE_ICON;
  const Fallback =
    fallbackIcon && !removed && BADGE_ICONS[fallbackIcon] ? BADGE_ICONS[fallbackIcon] : null;
  // The default counts as shown: it is what the customer sees, so it can be
  // removed like a chosen icon.
  const shown = icon ?? (Fallback ? fallbackIcon : null);

  // Radix hands back '' when the chosen item is clicked again, which is the
  // quickest way to clear; both routes store the same cleared value so the
  // API sees a removal.
  const cleared = fallbackIcon ? NO_FEATURE_ICON : null;
  const choose = key => {
    iconHelpers.setValue(key || cleared);
    setOpen(false);
  };

  return (
    <div className="relative">
      {multiline ? (
        <Textarea
          id={name}
          name={name}
          // One line until there is text: the box sizes to its content.
          rows={1}
          value={textField.value ?? ''}
          onChange={textField.onChange}
          onBlur={textField.onBlur}
          placeholder={placeholder}
          aria-label={ariaLabel}
          maxLength={maxLength}
          className="min-h-9 resize-none pl-9"
        />
      ) : (
        <Input
          id={name}
          name={name}
          type="text"
          value={textField.value ?? ''}
          onChange={textField.onChange}
          onBlur={textField.onBlur}
          placeholder={placeholder}
          aria-label={ariaLabel}
          maxLength={maxLength}
          className="pl-9"
        />
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={
              shown
                ? `Change icon (${ICON_LABELS[shown]})${ariaLabel ? ` for ${ariaLabel.toLowerCase()}` : ''}`
                : `Add an icon${ariaLabel ? ` to ${ariaLabel.toLowerCase()}` : ''}`
            }
            className={cn(
              'absolute left-2 flex h-5 w-5 items-center justify-center rounded-md transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
              multiline ? 'top-2' : 'top-1/2 -translate-y-1/2',
              icon
                ? 'text-chocolate-600 hover:bg-chocolate-600/10'
                : Fallback
                  ? 'text-gray-400 dark:text-muted-foreground hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-600 dark:hover:text-muted-foreground'
                  : 'border border-dashed border-gray-300 dark:border-border text-gray-400 dark:text-muted-foreground hover:border-gray-400 dark:hover:border-border hover:text-gray-600 dark:hover:text-muted-foreground',
              open &&
                (icon
                  ? 'bg-chocolate-600/10'
                  : Fallback
                    ? 'bg-gray-100 dark:bg-muted text-gray-600 dark:text-muted-foreground'
                    : 'border-gray-400 dark:border-border text-gray-600 dark:text-muted-foreground'),
            )}
          >
            {Icon ? (
              <Icon aria-hidden="true" className="h-3.5 w-3.5" />
            ) : Fallback ? (
              <Fallback aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <Plus aria-hidden="true" className="h-3 w-3" strokeWidth={2.5} />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="w-auto p-2">
          <ToggleGroup
            type="single"
            value={icon ?? ''}
            onValueChange={choose}
            spacing={1}
            aria-label={pickerLabel}
            className="grid grid-cols-6 gap-0.5"
          >
            {BADGE_ICON_KEYS.map(key => {
              const Option = BADGE_ICONS[key];
              return (
                <ToggleGroupItem
                  key={key}
                  value={key}
                  aria-label={ICON_LABELS[key]}
                  className="h-8 w-8 rounded-md text-gray-600 dark:text-muted-foreground hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-900 dark:hover:text-foreground data-[state=on]:bg-chocolate-600/10 data-[state=on]:text-chocolate-600"
                >
                  <Option aria-hidden="true" className="h-4 w-4" />
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
          {shown && (
            <button
              type="button"
              onClick={() => choose(null)}
              className="mt-2 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-500 dark:text-muted-foreground transition-colors hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-800 dark:hover:text-foreground"
            >
              <X aria-hidden="true" className="h-3 w-3" />
              Remove icon
            </button>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
